import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  CreditCard,
  Download,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  UserRoundX,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LocalizedName, localizedName } from '@/components/localized-name';
import { RecordPaymentDialog } from '@/features/events/record-payment-dialog';
import { paymentMethodLabelKey } from '@/features/events/payment-methods';
import { cn } from '@/lib/utils';
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPhone,
  initials,
} from '@/lib/format';
import { SubscriptionDetailSheet } from './subscription-detail-sheet';
import { SubscriptionFormSheet } from './subscription-form-sheet';
import {
  OriginCell,
  PaidCell,
  PeriodBadge,
  BenefitsLabel,
  originLabelKey,
} from './subscription-cells';
import {
  useBookingLocations,
  useCancelSubscription,
  useExportSubscriptions,
  useSubscriptionPlans,
  useSubscriptionSearch,
  type SubscriptionSearchModel,
} from './subscriptions.api';
import {
  DATE_BASIS,
  ORIGIN,
  PAYMENT_STATE,
  SORT,
  STATUS_FILTER,
  type SubscriptionRow,
} from './types';

/**
 * Subscriptions — one row per person enrolled in a plan.
 *
 * No date picker, following the original: this page answers "what is true right
 * now", and a range plus a "counted by" selector was four ways to misread a
 * number. The API still takes a window, so the page sends the current month and
 * says so rather than offering a control anyone can misconfigure.
 */

const ANY = 'ANY';

export function ManageSubscriptionsPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const isEnglish = language === 'en';

  /*
   * Read once, as the initial state: after that the controls own it, and
   * re-reading would fight them.
   */
  const [params] = useSearchParams();
  const seed = (key: string, fallback: string) => params.get(key) ?? fallback;

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [locationId, setLocationId] = useState(() => seed('locationId', ANY));
  const [subscriptionId, setSubscriptionId] = useState(() =>
    seed('subscriptionId', ANY)
  );
  const [status, setStatus] = useState(() =>
    seed('status', String(STATUS_FILTER.all))
  );
  const [origin, setOrigin] = useState(() => seed('origin', ANY));
  const [paymentState, setPaymentState] = useState(() =>
    seed('paymentState', String(PAYMENT_STATE.all))
  );
  const [sort, setSort] = useState(String(SORT.createdDesc));
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  /** `null` closed, a row to edit, `{}` to create. */
  const [editing, setEditing] = useState<
    SubscriptionRow | Record<string, never> | null
  >(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [paying, setPaying] = useState<SubscriptionRow | null>(null);
  const [cancelling, setCancelling] = useState<SubscriptionRow | null>(null);

  const { data: locations } = useBookingLocations();
  const { data: plans } = useSubscriptionPlans();

  const model: SubscriptionSearchModel = useMemo(
    () => ({
      // No start or end date: this page has no date filter, so every
      // membership is in scope. `dateBasis` only bites once one is sent.
      dateBasis: DATE_BASIS.created,
      locationId: locationId === ANY ? null : locationId,
      subscriptionId: subscriptionId === ANY ? null : subscriptionId,
      // Unlike most filters here, 0 means "all" rather than being omitted.
      status: Number(status),
      origin: origin === ANY ? null : Number(origin),
      paymentState: Number(paymentState),
      autoRenew: null,
      includeMemberSeats: true,
      query,
      sort: Number(sort),
      page,
      size,
    }),
    [
      locationId,
      subscriptionId,
      status,
      origin,
      paymentState,
      sort,
      query,
      page,
      size,
    ]
  );

  const search = useSubscriptionSearch(model);
  const exportRows = useExportSubscriptions();
  const cancel = useCancelSubscription();

  const rows = search.data?.memberships ?? [];
  const total = search.data?.total ?? 0;

  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const filterFields: FilterField[] = [
    {
      id: 'location',
      label: t('manageSubscriptions.location'),
      value: locationId,
      onChange: reset(setLocationId),
      options: [
        { value: ANY, label: t('manageSubscriptions.any') },
        ...(locations ?? []).map((l) => ({
          value: l.id,
          label: localizedName(l, language) || (l.name ?? ''),
        })),
      ],
    },
    {
      id: 'plan',
      label: t('manageSubscriptions.plan'),
      value: subscriptionId,
      onChange: reset(setSubscriptionId),
      options: [
        { value: ANY, label: t('manageSubscriptions.any') },
        ...(plans ?? []).map((p) => ({
          value: p.id,
          label: localizedName(p, language),
        })),
      ],
    },
    {
      id: 'status',
      label: t('manageSubscriptions.status'),
      value: status,
      onChange: reset(setStatus),
      // All / Active / Expiring / Expired / Cancelled is a lifecycle.
      sort: false,
      resetValue: String(STATUS_FILTER.all),
      options: [
        { value: String(STATUS_FILTER.all), label: t('manageSubscriptions.all') },
        { value: String(STATUS_FILTER.active), label: t('manageSubscriptions.active') },
        { value: String(STATUS_FILTER.expiring), label: t('manageSubscriptions.expiringSoon') },
        { value: String(STATUS_FILTER.expired), label: t('manageSubscriptions.expired') },
        { value: String(STATUS_FILTER.cancelled), label: t('manageSubscriptions.cancelled') },
      ],
    },
    {
      id: 'origin',
      label: t('manageSubscriptions.originLabel'),
      value: origin,
      onChange: reset(setOrigin),
      sort: false,
      options: [
        { value: ANY, label: t('manageSubscriptions.any') },
        ...Object.values(ORIGIN).map((value) => ({
          value: String(value),
          label: t(originLabelKey(value)),
        })),
      ],
    },
    {
      id: 'paymentState',
      label: t('manageSubscriptions.paidHow'),
      value: paymentState,
      onChange: reset(setPaymentState),
      sort: false,
      resetValue: String(PAYMENT_STATE.all),
      options: [
        { value: String(PAYMENT_STATE.all), label: t('manageSubscriptions.any') },
        { value: String(PAYMENT_STATE.paidOnline), label: t('manageSubscriptions.paidOnline') },
        { value: String(PAYMENT_STATE.paidOffline), label: t('manageSubscriptions.paidOffline') },
        { value: String(PAYMENT_STATE.belowList), label: t('manageSubscriptions.belowList') },
        { value: String(PAYMENT_STATE.unpaid), label: t('manageSubscriptions.unbilled') },
        { value: String(PAYMENT_STATE.sponsored), label: t('manageSubscriptions.sponsored') },
      ],
    },
    {
      // Sort is declared as a filter field rather than a control beside the
      // table, so it moves into the mobile sheet with everything else instead
      // of being special-cased the way the original had to.
      id: 'sort',
      label: t('manageSubscriptions.sort'),
      value: sort,
      onChange: reset(setSort),
      sort: false,
      resetValue: String(SORT.createdDesc),
      options: [
        { value: String(SORT.createdDesc), label: t('manageSubscriptions.sortCreatedDesc') },
        { value: String(SORT.createdAsc), label: t('manageSubscriptions.sortCreatedAsc') },
        { value: String(SORT.endingSoonest), label: t('manageSubscriptions.sortEndingSoonest') },
        { value: String(SORT.endingLatest), label: t('manageSubscriptions.sortEndingLatest') },
        { value: String(SORT.name), label: t('manageSubscriptions.sortName') },
        { value: String(SORT.paidDesc), label: t('manageSubscriptions.sortPaidDesc') },
      ],
    },
  ];

  function runExport() {
    exportRows.mutate(model, {
      onSuccess: (data) => {
        if (!data?.length) {
          toast.info(t('manageSubscriptions.nothingToExport'));
          return;
        }
        const keys = Object.keys(data[0]);
        const escape = (value: unknown) =>
          `"${String(value ?? '').replace(/"/g, '""')}"`;
        const csv = [
          keys.join(','),
          ...data.map((row) => keys.map((key) => escape(row[key])).join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'subscriptions.csv';
        link.click();
        URL.revokeObjectURL(url);
      },
      onError: () => toast.error(t('manageSubscriptions.exportFailed')),
    });
  }

  /** The customer-facing name, with the internal one above it. */
  const PlanName = ({ row }: { row: SubscriptionRow }) => (
    <LocalizedName
      nameEn={row.subscriptionNameEn}
      nameEs={row.subscriptionNameEs}
      internalName={row.subscriptionInternalName}
    />
  );

  const locationName = (row: SubscriptionRow) =>
    (isEnglish ? row.locationNameEn : row.locationNameEs) ?? '';

  /** The same three actions in both layouts, so they cannot drift. */
  const RowMenu = ({ row }: { row: SubscriptionRow }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t('manageSubscriptions.actions')}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => setEditing(row)}>
          <Pencil />
          {t('manageSubscriptions.edit')}
        </DropdownMenuItem>
        <DropdownMenuItem
          // A seat is paid for by whoever bought the plan.
          disabled={row.isMemberSeat}
          onSelect={() => setPaying(row)}
        >
          <CreditCard />
          {t('manageSubscriptions.recordPayment')}
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="destructive"
          onSelect={() => setCancelling(row)}
        >
          <UserRoundX />
          {t('manageSubscriptions.cancelSubscription')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('manageSubscriptions.title')}
        description={t('manageSubscriptions.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={runExport}
              disabled={exportRows.isPending}
            >
              <Download />
              <span className="hidden sm:inline">
                {t('manageSubscriptions.export')}
              </span>
            </Button>
            <Button size="sm" onClick={() => setEditing({})}>
              <Plus />
              <span className="hidden sm:inline">
                {t('manageSubscriptions.newSubscription')}
              </span>
            </Button>
          </>
        }
      />

      <FilterBar
        busy={search.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void search.refetch();
              setQuery(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('manageSubscriptions.searchPlaceholder')}
                aria-label={t('manageSubscriptions.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
        summary={
          search.isSuccess ? (
            <p className="text-muted-foreground text-sm">
              {t('manageSubscriptions.showingOf', {
                shown: rows.length,
                total,
              })}
            </p>
          ) : null
        }
      />

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('manageSubscriptions.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => void search.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-8 shrink-0 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-3 w-56" />
              </div>
              <Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      )}

      {search.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('manageSubscriptions.noResults')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="hidden rounded-xl border lg:block">
            <Table busy={search.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('manageSubscriptions.user')}</TableHead>
                  <TableHead>{t('manageSubscriptions.plan')}</TableHead>
                  <TableHead>{t('manageSubscriptions.period')}</TableHead>
                  <TableHead>{t('manageSubscriptions.originLabel')}</TableHead>
                  <TableHead className="text-right">
                    {t('manageSubscriptions.paid')}
                  </TableHead>
                  <TableHead className="text-center">
                    {t('manageSubscriptions.benefits')}
                  </TableHead>
                  <TableHead className="text-center">
                    {t('manageSubscriptions.bookings')}
                  </TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <Avatar className="size-8 shrink-0">
                          {row.user?.avatar && (
                            <AvatarImage src={row.user.avatar} alt="" />
                          )}
                          <AvatarFallback>
                            {initials(row.user?.firstName, row.user?.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {row.user?.completeName ?? '—'}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">
                            {row.user?.email}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {formatPhone(row.user?.phoneNumber)}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <PlanName row={row} />
                      <p className="text-muted-foreground text-xs">
                        {locationName(row)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="whitespace-nowrap">
                        {formatDate(row.startDate)} – {formatDate(row.endDate)}
                      </p>
                      <div className="mt-0.5">
                        <PeriodBadge row={row} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <OriginCell row={row} />
                    </TableCell>
                    <TableCell className="text-right">
                      <PaidCell row={row} />
                    </TableCell>
                    <TableCell className="text-center tabular-nums">
                      <BenefitsLabel row={row} />
                    </TableCell>
                    <TableCell className="text-center tabular-nums">
                      {formatNumber(row.bookingsCount)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="sm" onClick={() => setDetailId(row.id)}>
                          {t('manageSubscriptions.view')}
                        </Button>
                        <RowMenu row={row} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={search.isFetching} />
            <div
              className={cn(
                'space-y-2',
                search.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((row) => (
                <div
                  key={row.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <Avatar className="size-8 shrink-0">
                        {row.user?.avatar && (
                          <AvatarImage src={row.user.avatar} alt="" />
                        )}
                        <AvatarFallback>
                          {initials(row.user?.firstName, row.user?.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {row.user?.completeName ?? '—'}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {row.user?.email}
                        </p>
                      </div>
                    </div>
                    <RowMenu row={row} />
                  </div>

                  <div>
                    <PlanName row={row} />
                    <p className="text-muted-foreground text-xs">
                      {locationName(row)} · {formatDate(row.startDate)} –{' '}
                      {formatDate(row.endDate)}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <PeriodBadge row={row} />
                    <OriginCell row={row} />
                  </div>

                  <div className="flex items-end justify-between gap-3">
                    <p className="text-muted-foreground text-xs">
                      {t('manageSubscriptions.benefits')}:{' '}
                      <BenefitsLabel row={row} /> · {t('manageSubscriptions.bookings')}
                      : {formatNumber(row.bookingsCount)}
                    </p>
                    <PaidCell row={row} />
                  </div>

                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => setDetailId(row.id)}
                  >
                    {t('manageSubscriptions.view')}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      <SubscriptionDetailSheet
        subscriptionId={detailId}
        open={detailId !== null}
        onOpenChange={(next) => {
          if (!next) setDetailId(null);
        }}
        onRecordPayment={setPaying}
      />

      <SubscriptionFormSheet
        row={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        onSaved={(newId) => {
          void search.refetch();
          // Land on what was just created rather than hunting the list for it.
          if (newId) setDetailId(newId);
        }}
      />

      <RecordPaymentDialog
        targetId={paying?.id ?? null}
        purchases={[]}
        endpoint="memberships"
        // A subscription carries no running balance, so what is still owed has
        // to be derived from the plan's list price less what has been paid.
        seedAmount={
          paying ? (paying.listPrice ?? 0) - (paying.paidTotal ?? 0) : null
        }
        renderEcho={({ amount, method }) => (
          <>
            {t('manageSubscriptions.echoRecordPayment', {
              amount: formatCurrency(amount),
              method: t(paymentMethodLabelKey(method)).toLowerCase(),
              name: paying?.user?.completeName ?? '',
            })}
            {amount < (paying?.listPrice ?? 0) && (
              <>
                {' '}
                {t('manageSubscriptions.echoListPriceIs')}{' '}
                {formatCurrency(paying?.listPrice)},{' '}
                {t('manageSubscriptions.echoLoggedAsDiscount', {
                  amount: formatCurrency((paying?.listPrice ?? 0) - amount),
                })}
              </>
            )}
          </>
        )}
        open={paying !== null}
        onOpenChange={(next) => {
          if (!next) setPaying(null);
        }}
        onSaved={() => void search.refetch()}
      />

      <AlertDialog
        open={cancelling !== null}
        onOpenChange={(next) => {
          if (!next) setCancelling(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('manageSubscriptions.cancelSubscription')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('manageSubscriptions.cancelConfirmBody', {
                name: cancelling?.user?.completeName ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                cancel.mutate(cancelling!.id, {
                  // Succeeds with an empty message, hence the fallback.
                  onSuccess: () => {
                    toast.success(t('manageSubscriptions.cancelledToast'));
                    setCancelling(null);
                  },
                  onError: () =>
                    toast.error(t('manageSubscriptions.actionFailed')),
                })
              }
            >
              {t('manageSubscriptions.cancelSubscription')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
